import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import { SubmittedFormModel } from "app/services/api/types/forms/models/SubmittedFormModel";
import { FormHistoryResponse } from "app/services/api/types/forms/responses/FormHistoryResponse";
import { RootState } from "app/services/store";

type ResidentId = number;
type EntriesData = SubmittedFormModel[];

interface State {
  entries: Record<ResidentId, EntriesData>;
}
const initialState: State = {
  entries: {},
};

export const formEntriesSlice = createSlice({
  name: "formEntries",
  initialState,
  reducers: {
    storeFormEntries: (state, action: PayloadAction<FormHistoryResponse>) => {
      const { data } = action.payload;
      data.forEach((entry) => {
        const residentId = entry.resident.id;
        if (!state.entries[residentId]) {
          state.entries[residentId] = [];
        }

        // Check if the entry already exists
        const exists = state.entries[residentId].some(
          (existingEntry) => existingEntry.id === entry.id,
        );
        if (!exists) {
          state.entries[residentId].push(entry);
        }
      });
    },
  },
});

export const { storeFormEntries } = formEntriesSlice.actions;

// Create a base selector for getting resident entries
const selectResidentEntries = createSelector(
  [
    (state: RootState) => state.formEntries?.entries,
    (_: RootState, residentId?: number) => residentId,
  ],
  (entries, residentId) => (!residentId ? [] : entries?.[residentId] || []),
);

export const selectResidentFormsByFilter = createSelector(
  [
    selectResidentEntries,
    (
      _: RootState,
      _residentId: number | undefined,
      formTag: string | undefined,
    ) => formTag,
  ],
  (residentEntries, formTag) => {
    if (!formTag) return residentEntries;

    // Sort by ID to maintain consistent order
    return residentEntries
      .filter((entry) => entry.form.tag === formTag)
      .sort((a, b) => b.id - a.id);
  },
);

export default formEntriesSlice.reducer;
